import { portalFetch } from '../../shared/auth/portalSession';
import { API_BASE_URL } from './config';
import { getAuthHeaders, requireAuthentication } from './authUtils';

export interface TechnicianApiPayload {
  name: string;
  email?: string;
  phone?: string;
  supportedServiceType: 'US' | 'Xray';
  supportedServiceIds: string[];
  defaultConcurrency: number;
  isActive?: boolean;
}

export interface TechnicianApiResponse {
  technicianId: string;
  name: string;
  email?: string;
  phone?: string;
  supportedServiceType: 'US' | 'Xray';
  supportedServiceIds: string[];
  defaultConcurrency: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Helper to convert API response to frontend Technician type
function convertApiToTechnician(apiTech: TechnicianApiResponse) {
  return {
    id: apiTech.technicianId,
    name: apiTech.name,
    email: apiTech.email,
    phone: apiTech.phone,
    supportedServiceType: apiTech.supportedServiceType,
    supportedServiceIds: apiTech.supportedServiceIds,
    defaultConcurrency: apiTech.defaultConcurrency,
    isActive: apiTech.isActive,
    createdAt: apiTech.createdAt,
    updatedAt: apiTech.updatedAt,
  };
}

// Helper to convert frontend Technician to API payload
function convertTechnicianToApi(technician: Omit<TechnicianApiPayload, 'isActive'>): TechnicianApiPayload {
  return {
    name: technician.name,
    email: technician.email,
    phone: technician.phone,
    supportedServiceType: technician.supportedServiceType,
    supportedServiceIds: technician.supportedServiceIds,
    defaultConcurrency: technician.defaultConcurrency,
    isActive: true,
  };
}

export async function getTechniciansApi() {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/technicians`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch technicians: ${response.statusText}`);
    }

    const data: TechnicianApiResponse[] = await response.json();
    return data.map(convertApiToTechnician);
  } catch (error) {
    console.error('Error fetching technicians:', error);
    throw error;
  }
}

export async function getTechnicianByIdApi(id: string) {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/technicians/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch technician: ${response.statusText}`);
    }

    const data: TechnicianApiResponse = await response.json();
    return convertApiToTechnician(data);
  } catch (error) {
    console.error('Error fetching technician:', error);
    throw error;
  }
}

export async function createTechnicianApi(technician: Omit<TechnicianApiPayload, 'isActive'>) {
  requireAuthentication();
  
  try {
    const payload = convertTechnicianToApi(technician);
    
    const response = await portalFetch('admin', `${API_BASE_URL}/technicians`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create technician: ${response.statusText}`);
    }

    const data: TechnicianApiResponse = await response.json();
    return convertApiToTechnician(data);
  } catch (error) {
    console.error('Error creating technician:', error);
    throw error;
  }
}

export async function updateTechnicianApi(id: string, updates: Partial<TechnicianApiPayload>) {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/technicians/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update technician: ${response.statusText}`);
    }

    const data: TechnicianApiResponse = await response.json();
    return convertApiToTechnician(data);
  } catch (error) {
    console.error('Error updating technician:', error);
    throw error;
  }
}

export async function deleteTechnicianApi(id: string) {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/technicians/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to delete technician: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting technician:', error);
    throw error;
  }
}
